<template>
  <section class="flex flex-col gap-4 w-full">
    <div
      class="w-full bg-white p-6 rounded-md shadow-md flex flex-wrap gap-2 items-center justify-between"
    >
      <p class="font-[800] text-neutral-900">
        {{ questionCount }}.{{ " " + question?.prompt }}
      </p>
      <MessageVoice
        v-if="playAudio"
        :msg="msgVoice(question.prompt)"
        :scaleIcons="0.8"
        :showIconsName="false"
        @onEndMessage="onSpeak($event)"
      />
    </div>

    <div
      class="bg-white p-6 rounded-md shadow-md flex justify-center items-center min-h-48"
    >
      <Draggable
        v-model="availableOptions"
        item-key="id"
        group="answers"
        class="flex flex-wrap gap-2 w-full min-h-48"
        :sort="false"
        :clone="cloneOption"
        :move="checkMove"
        id="list-options"
      >
        <template #item="{ element }">
          <div
            class="px-4 py-2 border border-h-primary-300 rounded-md text-h-info-700 cursor-move bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            {{ element.name }}
          </div>
        </template>
      </Draggable>
    </div>

    <div class="">
      <div
        v-for="(item, index) in processedItems"
        :key="item.id"
        class="mb-6 last:mb-0 bg-white p-6 rounded-md shadow-md flex flex-row items-center gap-2"
      >
        <span class="font-bold">{{ renderLetterItem(index) }}</span>
        <div class="flex flex-row flex-wrap items-center gap-2">
          <template v-for="(part, index) in item.parts" :key="index">
            <span v-if="part.type === 'text'" v-html="part.content"></span>

            <Draggable
              v-else
              v-model="part.selectedOption"
              group="answers"
              class="min-w-[120px] inline-block min-h-[36px]"
              :class="[
                {
                  'border  rounded-md border-h-neutral-300 bg-neutral-50':
                    part.selectedOption.length === 0,
                },
              ]"
              item-key="id"
              :move="checkMove"
            >
              <template #item="{ element }">
                <div
                  class="px-2 py-2 border-h-primary-300 rounded-md border bg-gray-50 text-h-info-700"
                >
                  {{ element.name }}
                </div>
              </template>
            </Draggable>
          </template>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from "vue";

interface QuestionPart {
  type: string;
  content?: string;
  correctId?: string;
  selectedOption: any[];
}

interface ProcessedItem {
  id: string;
  parts: QuestionPart[];
}

export default defineComponent({
  name: "QuestionDragDropComponent",
  data() {
    return {
      playAudio: true as boolean,
    };
  },
  props: {
    question: {
      type: Object,
      required: true,
    },
    answers: {
      type: Object,
      required: true,
    },
    questionCount: {
      type: Number,
      required: true,
    },
  },
  emits: ["onSpeak"],
  setup(props, { emit }) {
    const availableOptions = ref(
      props.question.options.map((opt: string) => JSON.parse(opt))
    );

    const processedItems = ref<ProcessedItem[]>(
      props.question.items.map((item: any) => {
        const cleanText = item.answerText.replace(/^<p>|<\/p>$/g, "");
        const parts: QuestionPart[] = [];

        cleanText
          .split(/\[resposta-hidden:([\w-]+)\]/)
          .forEach((segment: string, index: number) => {
            if (index % 2 === 0) {
              parts.push({
                type: "text",
                content: segment,
                selectedOption: [],
              });
            } else {
              parts.push({
                type: "gap",
                correctId: segment,
                selectedOption: [],
              });
            }
          });

        return {
          id: item.id,
          parts,
        };
      })
    );

    const cloneOption = (option: any) => ({
      ...option,
      id: option.id,
    });

    const isFilledBlankSpaces = computed(() => {
      return processedItems.value.every((item) => {
        return item.parts.every((part) => {
          return part.type === "gap" ? part.selectedOption.length > 0 : true;
        });
      });
    });

    function fillAnswer() {
      props.answers.dragAndDropAnswers = [];
      processedItems.value.forEach((item) => {
        item.parts.forEach((part) => {
          if (part.type === "gap") {
            part.selectedOption[0].id = part.correctId;
            const response = JSON.stringify(part.selectedOption[0]);
            props.answers.dragAndDropAnswers.push(response);
          }
        });
      });
    }
    function renderLetterItem(index: number) {
      return `${String.fromCharCode(65 + index)}:`;
    }
    function msgVoice(prompt: string): string {
      let result = prompt;
      let alphabetIndex = 0;

      props.question?.items.forEach((element) => {
        const letter = String.fromCharCode(65 + alphabetIndex);

        const plainText = element.answerText
          .replace(/<[^>]*>/g, " ")
          .replace(/\[resposta-hidden:[\w-]+\]/g, " Lacuna a ser preenchida ")
          .trim();

        result += `. Item ${letter}: ${plainText}. `;
        alphabetIndex++;
      });

      return result;
    }
    function onSpeak(event: any): void {
      emit("onSpeak", event);
    }

    function checkMove(e: any): boolean {
      if (e.to.id === "list-options") return true;
      return e.relatedContext.list.length < 1;
    }
    watch(processedItems.value, (val) => {
      const isFilledBlanks = val.every((item) =>
        item.parts.every((part) =>
          part.type === "gap" ? part.selectedOption.length > 0 : true
        )
      );
      if (isFilledBlanks) {
        fillAnswer();
      } else {
        props.answers.dragAndDropAnswers = null;
      }
    });
    return {
      availableOptions,
      processedItems,
      cloneOption,
      isFilledBlankSpaces,
      renderLetterItem,
      msgVoice,
      onSpeak,
      checkMove,
    };
  },
});
</script>

<style scoped>
.drag-handle {
  cursor: move;
}
</style>
